import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import ErrorBoundary from '../components/ErrorBoundary';

// Import all widgets from the widget registry
import StatsWidget from '../components/widgets/StatsWidget';
import RevenueWidget from '../components/widgets/RevenueWidget';
import EntriesWidget from '../components/widgets/EntriesWidget';
import TeamWidget from '../components/widgets/TeamWidget';
import AnalyticsWidget from '../components/widgets/AnalyticsWidget';
import PipelineWidget from '../components/widgets/PipelineWidget';

const WIDGET_COMPONENTS = {
  stats: StatsWidget,
  'recent-entries': EntriesWidget,
  revenue: RevenueWidget,
  team: TeamWidget,
  analytics: AnalyticsWidget,
  pipeline: PipelineWidget
};

const WIDGET_TITLES = {
  stats: 'Statistics',
  'recent-entries': 'Recent Entries',
  revenue: 'Revenue',
  team: 'Team',
  analytics: 'Analytics',
  pipeline: 'Sales Pipeline'
};

const Dashboard = () => {
  const { user: authUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [darkMode, setDarkMode] = useState(true);
  const [locked, setLocked] = useState(() => {
    const saved = localStorage.getItem('ngx-mc-dashboard-locked');
    return saved !== null ? saved === 'true' : true; // Default to locked
  });
  
  // Mobile detection
  const [windowWidth, setWindowWidth] = useState(() => typeof window !== 'undefined' ? window.innerWidth : 1200);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const isMobile = windowWidth < 800;
  const isTablet = windowWidth >= 800 && windowWidth < 1400;
  
  // Widget order - load from localStorage or use default
  // Merge any new widgets that aren't in the saved order so they appear automatically
  const [widgetOrder, setWidgetOrder] = useState(() => {
    const saved = localStorage.getItem('ngx-mc-dashboard-order');
    if (!saved) return Object.keys(WIDGET_COMPONENTS);
    const parsed = JSON.parse(saved);
    const allKeys = Object.keys(WIDGET_COMPONENTS);
    // Add any new widgets not in the saved order
    const merged = [...parsed, ...allKeys.filter(k => !parsed.includes(k))];
    if (merged.length !== parsed.length) {
      localStorage.setItem('ngx-mc-dashboard-order', JSON.stringify(merged));
    }
    return merged;
  });
  
  // Legacy: also check old enabledWidgets key
  const [enabledWidgets, setEnabledWidgets] = useState(() => {
    const saved = localStorage.getItem('ngx-mc-widgets');
    if (!saved) return Object.keys(WIDGET_COMPONENTS);
    const parsed = JSON.parse(saved);
    const allKeys = Object.keys(WIDGET_COMPONENTS);
    const merged = [...parsed, ...allKeys.filter(k => !parsed.includes(k))];
    if (merged.length !== parsed.length) {
      localStorage.setItem('ngx-mc-widgets', JSON.stringify(merged));
    }
    return merged;
  });

  // Load user widget permissions from server (non-superadmins)
  useEffect(() => {
    if (authUser && authUser.role !== 'superadmin') {
      fetch('/api/users/' + authUser.id + '/widgets', { credentials: 'include' })
        .then(r => r.json())
        .then(data => {
          if (data.widgets && data.widgets.length > 0) {
            setEnabledWidgets(data.widgets);
            setWidgetOrder(data.widgets);
            localStorage.setItem('ngx-mc-widgets', JSON.stringify(data.widgets));
            localStorage.setItem('ngx-mc-dashboard-order', JSON.stringify(data.widgets));
          }
        })
        .catch(console.error);
    }
  }, [authUser]);

  useEffect(() => {
    const checkDarkMode = () => {
      const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
      setDarkMode(isDark);
    };
    
    checkDarkMode();
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    
    const timer = setTimeout(() => setLoading(false), 500);
    
    return () => {
      observer.disconnect();
      clearTimeout(timer);
    };
  }, []);

  // Save widget order when it changes (only for superadmins)
  useEffect(() => {
    if (authUser?.role === 'superadmin') {
      localStorage.setItem('ngx-mc-dashboard-order', JSON.stringify(widgetOrder));
      localStorage.setItem('ngx-mc-widgets', JSON.stringify(widgetOrder));
    }
  }, [widgetOrder, authUser]);

  // Save lock state
  useEffect(() => {
    if (authUser?.role === 'superadmin') {
      localStorage.setItem('ngx-mc-dashboard-locked', String(locked));
    }
  }, [locked, authUser]);

  // Move widget up/down (simple reordering without drag-drop)
  const moveWidget = (index, direction) => {
    if (locked) return;
    const newOrder = [...widgetOrder];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= newOrder.length) return;
    
    // Swap
    [newOrder[index], newOrder[newIndex]] = [newOrder[newIndex], newOrder[index]];
    setWidgetOrder(newOrder);
  };

  const toggleLock = () => {
    setLocked(!locked);
  };

  const textColor = darkMode ? '#f3f4f6' : '#111827';
  const subTextColor = darkMode ? '#9ca3af' : '#6b7280';
  const headerBg = darkMode ? '#1f2937' : '#ffffff';
  const widgetBg = darkMode ? '#374151' : '#ffffff';
  const borderColor = darkMode ? '#4b5563' : '#e5e7eb';
  
  // Get current date formatted
  const today = new Date();
  const dateStr = today.toLocaleDateString('en-US', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  // Get widget components in saved order
  const enabledComponents = widgetOrder
    .filter(w => WIDGET_COMPONENTS[w])
    .map(w => ({ id: w, Component: WIDGET_COMPONENTS[w], title: WIDGET_TITLES[w] || w }));

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 40, color: textColor }}>Loading...</div>;
  }

  return (
    <div>
      <div className="page-header" style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ color: textColor, margin: 0 }}>Dashboard</h1>
          <span style={{ color: subTextColor, fontSize: 14 }}>{dateStr}</span>
        </div>
        {authUser?.role === 'superadmin' && (
          <button
            onClick={toggleLock}
            style={{
              padding: '8px 16px',
              backgroundColor: locked ? '#10b981' : '#6b7280',
              color: 'white',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: 6
            }}
          >
            {locked ? '🔒 Locked' : '🔓 Unlocked'}
          </button>
        )}
      </div>
      
      {authUser?.role === 'superadmin' && !locked && (
        <div style={{ 
          padding: '8px 16px', 
          margin: '0 16px 16px 16px', 
          backgroundColor: darkMode ? '#1f2937' : '#fef3c7',
          borderRadius: 6,
          fontSize: 13
        }}>
          💡 Drag widgets to reorder, or use ↑↓ buttons below each widget. Position is saved automatically.
        </div>
      )}
      
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: isMobile ? 12 : 16, padding: isMobile ? 8 : 16 }}>
        {enabledComponents.map(({ id, Component, title }, index) => (
          <div 
            key={id} 
            style={{ 
              flex: isMobile ? '1 1 100%' : '1 1 calc(50% - 8px)',
              minWidth: '300px',
              overflow: 'visible',
              position: 'relative'
            }}
          >
            {/* Reorder controls for superadmins when unlocked */}
            {authUser?.role === 'superadmin' && !locked && (
              <div style={{
                position: 'absolute',
                top: 4,
                right: 4,
                zIndex: 10,
                display: 'flex',
                gap: 2,
                opacity: 0.7
              }}>
                <button
                  onClick={() => moveWidget(index, -1)}
                  disabled={index === 0}
                  style={{
                    padding: '2px 6px',
                    fontSize: 12,
                    backgroundColor: '#4b5563',
                    color: 'white',
                    border: 'none',
                    borderRadius: 3,
                    cursor: index === 0 ? 'not-allowed' : 'pointer',
                    opacity: index === 0 ? 0.3 : 1
                  }}
                  title="Move up"
                >
                  ↑
                </button>
                <button
                  onClick={() => moveWidget(index, 1)}
                  disabled={index === enabledComponents.length - 1}
                  style={{
                    padding: '2px 6px',
                    fontSize: 12,
                    backgroundColor: '#4b5563',
                    color: 'white',
                    border: 'none',
                    borderRadius: 3,
                    cursor: index === enabledComponents.length - 1 ? 'not-allowed' : 'pointer',
                    opacity: index === enabledComponents.length - 1 ? 0.3 : 1
                  }}
                  title="Move down"
                >
                  ↓
                </button>
              </div>
            )}
            {/* Wrap each widget in ErrorBoundary for graceful failure handling */}
            <ErrorBoundary title={title} darkMode={darkMode}>
              <Component darkMode={darkMode} />
            </ErrorBoundary>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Dashboard;
